// IMP-444 (#403): 목록 화면 Custom Header 컬럼 표시/숨김·순서 공통 SSoT (순수 util).
//
// Site/Product/Service/Error/User 5개 목록 화면이 각자의 컬럼 합성(권한별 컬럼 추가/제외 등)을
// 모두 끝낸 뒤, 마지막 단계에서 이 모듈을 한 번만 호출한다. 화면별 합성 로직을 여기로
// 끌어오지 않는다.
//
// [불변조건 — FR-007] RayTable.js 의 projection 생성부가 `columns.map(x => projects[x.name] = 1)` 로 서버
// 조회조건(projection)을 만든다. 따라서 "숨김"은 배열에서 컬럼을 제거하는 방식이 아니라
// `visible:false` 플래그로만 표현해야 한다. 제거 방식으로 되돌리면 링크·Export 가 깨진다.
// applyHeaderPrefs 의 반환 길이는 항상 입력 columns.length 와 같다.
//
// React/i18n/axios 비의존. 부수효과 없음. 어떤 입력에도 throw 하지 않는다.

/**
 * 화면이 합성을 끝낸 columns 와 저장된 saved 선호값을 병합해 최종 순서/표시 상태를 만든다.
 *
 * 정렬 규칙:
 * - saved 항목의 정렬키 = order 가 유한수이면 그 값, 아니면 saved 배열 내 인덱스.
 *   키가 같으면 saved 등장 순서(인덱스)를 tie-breaker 로 유지한다.
 * - saved 에 없는 columns 항목은 매칭된 항목들 뒤에, columns 원래 상대 순서로 이어붙인다.
 * - visible 은 saved 항목에 명시적으로 false 가 아니면 true (옛 저장값 하위 호환, FR-004).
 * - 원본 columns/saved/원소는 mutate 하지 않는다. 원소는 `{ ...col, visible }` 얕은 복사로
 *   만들어 화면이 주입한 onClick 등 함수 프로퍼티를 보존한다(JSON 복사 금지).
 *
 * @param {Array<object>} columns 화면이 합성을 끝낸 최종 컬럼 배열
 * @param {Array<object>} saved 저장된 헤더 선호값 (없거나 배열이 아니면 무시)
 * @returns {Array<object>} columns.length 와 동일한 길이의 resolved 배열
 */
export const applyHeaderPrefs = (columns, saved) => {
    const cols = Array.isArray(columns) ? columns : [];
    const savedList = Array.isArray(saved) ? saved : [];

    const savedByName = new Map();
    savedList.forEach((item, savedIndex) => {
        if (item && typeof item.name === 'string' && !savedByName.has(item.name)) {
            savedByName.set(item.name, { item, savedIndex });
        }
    });

    const matched = [];
    const unmatched = [];

    cols.forEach((col) => {
        const found = col?.name !== undefined ? savedByName.get(col.name) : undefined;
        if (found) {
            const { item, savedIndex } = found;
            const order = Number.isFinite(item.order) ? item.order : savedIndex;
            const visible = item.visible === false ? false : true;
            matched.push({ col, order, savedIndex, visible });
        } else {
            unmatched.push(col);
        }
    });

    matched.sort((a, b) => a.order - b.order || a.savedIndex - b.savedIndex);

    const resolvedMatched = matched.map(({ col, visible }) => ({ ...col, visible }));
    const resolvedUnmatched = unmatched.map((col) => ({ ...col, visible: true }));

    return [...resolvedMatched, ...resolvedUnmatched];
};

/**
 * resolved 중 visible !== false 인 항목만 순서대로 반환한다.
 * 비어있지 않은 입력의 필터 결과가 빈 배열이 되면(전 컬럼 숨김) 목록이 <th>/<td> 를 0개
 * 렌더하는 빈 표를 막기 위해 입력 전체를 순서 그대로 반환하고 console.warn 을 1회 남긴다.
 */
export const visibleHeaderColumns = (resolved) => {
    if (!Array.isArray(resolved)) return [];
    if (resolved.length === 0) return [];

    const visible = resolved.filter((c) => c?.visible !== false);
    if (visible.length === 0) {
        console.warn('[headerPrefs] all columns hidden — falling back to full list', {
            handler: 'visibleHeaderColumns',
            total: resolved.length,
            reason: 'no visible column remains',
        });
        return resolved;
    }
    return visible;
};

/**
 * name 컬럼을 지금 숨겨도 되는지 판정한다 (FR-008 최소 1개 표시).
 * 이미 숨겨진 컬럼을 대상으로 하면(끄는 동작이 아니므로) 항상 허용한다.
 */
export const canHideHeaderColumn = (resolved, name) => {
    if (!Array.isArray(resolved)) return false;
    const target = resolved.find((c) => c?.name === name);
    if (!target) return false;
    if (target.visible === false) return true;
    const visibleCount = resolved.filter((c) => c?.visible !== false).length;
    return visibleCount > 1;
};

/**
 * name 컬럼을 인접 항목과 위치 교환한다 (FR-002 순서 이동).
 * 대상이 없거나, 배열 경계를 벗어나거나, direction 이 'up'/'down' 이 아니면 무동작으로
 * 원본과 동일한 내용의 새 배열을 반환한다(에러 없음, mutate 없음).
 */
export const moveHeaderColumn = (resolved, name, direction) => {
    if (!Array.isArray(resolved)) return [];

    const copies = resolved.map((c) => ({ ...c }));
    const index = copies.findIndex((c) => c?.name === name);
    const delta = direction === 'up' ? -1 : direction === 'down' ? 1 : 0;
    if (index === -1 || delta === 0) return copies;

    const targetIndex = index + delta;
    if (targetIndex < 0 || targetIndex >= copies.length) return copies;

    const next = copies.slice();
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    return next;
};

/**
 * resolved 를 저장 가능한 형태로 정규화한다. order 는 배열 인덱스로 재부여한다.
 */
export const toSavedHeaderPrefs = (resolved) => {
    if (!Array.isArray(resolved)) return [];
    return resolved.map((col, index) => ({
        name: col?.name,
        label: col?.label ?? col?.name ?? '',
        type: col?.type ?? '',
        visible: col?.visible !== false,
        order: index,
    }));
};
